import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useData } from "@/contexts/DataContext";
import {
  TrendingUp,
  Briefcase,
  Link as LinkIcon,
  LogOut,
  Menu,
  X,
  LayoutDashboard, 
  Newspaper, 
  BarChart3,
  BookOpen,
  Activity
} from "lucide-react";

const sidebarItems = [
  { label: "Dashboard", path: "/admin", icon: LayoutDashboard },
  { label: "IPOs", path: "/admin/ipos", icon: TrendingUp },
  { label: "GMP Manager", path: "/admin/gmp", icon: BarChart3 },
  { label: "News", path: "/admin/news", icon: Newspaper },
  { label: "Jobs", path: "/admin/jobs", icon: Briefcase },
  { label: "Learning", path: "/admin/learning", icon: BookOpen },
  { label: "Affiliates", path: "/admin/affiliates", icon: LinkIcon },
  { label: "Market Ticker", path: "/admin/ticker", icon: Activity },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const { isAdmin, adminLogout, loading, authLoading } = useData();
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Check if user is authenticated, redirect to login if not
  useEffect(() => {
    // Only redirect after auth check is complete and user is not admin.
    if (!authLoading && !isAdmin) {
      navigate("/admin/login", { replace: true });
    }
  }, [isAdmin, navigate, authLoading]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Show loading spinner while checking auth/session and boot data.
  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderSidebarContent = () => (
    <>
      <div className="p-4 border-b border-border">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full hero-gradient flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-xs">D</span>
          </div>
          <div className="leading-tight">
            <span className="font-display font-bold text-sm text-foreground">Admin Panel</span>
            <span className="block text-[10px] text-muted-foreground">Dashboard</span>
          </div>
        </Link>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {sidebarItems.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <Icon size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <button
          onClick={async () => {
            try {
              await adminLogout();
            } catch (error) {
              console.error("Logout error:", error);
            }
          }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 w-full transition-colors"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="md:hidden sticky top-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <span className="font-display font-semibold text-foreground">Admin Panel</span>
        <button
          type="button"
          onClick={() => setMobileSidebarOpen((prev) => !prev)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-input bg-background text-foreground"
          aria-label={mobileSidebarOpen ? "Close menu" : "Open menu"}
        >
          {mobileSidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </header>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Close sidebar overlay"
          />
          <aside className="relative z-10 w-64 max-w-[85vw] h-full bg-card border-r border-border flex flex-col shadow-xl">
            {renderSidebarContent()}
          </aside>
        </div>
      )}

      <div className="flex min-h-[calc(100vh-57px)] md:min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex w-60 shrink-0 bg-card border-r border-border flex-col">
          {renderSidebarContent()}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
